import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { get } from 'lodash';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { BaseModalComponent } from 'common/src/modules/rnpl-common/components';
import { CompositeProductModel, ProductModel, ProductsService } from '../../../products';
import { ProductTypes } from '../../../products/product-types';
import { CompositeProductModalComponent } from '../../modals-products';
import { CreateGeneralProductModalComponent } from '../../modals-products/create-general-product-modal/create-general-product-modal.component';
import { CustomSearchFn, getProductSearchValue } from 'common/src/modules/rnpl-common/helpers';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { AccountingPositionModel, AccountingPositionsGroupModel } from 'projects/workspace/src/app/accounting/models';
import { ScheduledPaymentApiService } from 'projects/workspace/src/app/scheduled-payment/services/scheduled-payment-api.service';
import { CommonModalsActionsEnum, WarningModalComponent } from '../../modals-common';
import { DocumentTypesUppercaseEnum } from '../../modals-common/link-document-modal/enums/ducument-types.enum';
import { RecalculatePositionApiService } from '../../../../services/recalculate-position-api.service';
import { CHANGE_CORRECTION_CALCULATION_MODAL_DATA } from '../../modals.contsans';
import {
  CORRECTION_POSITION_TYPES_LIST,
  CURRENCIES_SYMBOLS_BY_ISO_CODE
} from 'projects/workspace/src/app/accounting/accounting.constants';
// import { PURPOSES_LIST } from 'projects/workspace/src/app/trade/trade.constants';
import { PurposeTypeEnum } from 'projects/workspace/src/app/purchase-order/enums';
import { PositionAccountingSettings } from 'projects/workspace/src/app/outgoing-invoice/models';
import { PositionAccountingSettingsComponent } from 'projects/workspace/src/app/shared/components';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { ScheduledPaymentModel } from 'projects/workspace/src/app/scheduled-payment/models';
import { PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { ReportingPeriodsPreferencesModel } from 'projects/workspace/src/app/reporting-periods/models';
import {
  DEFAULT_SALES_PRICE_LIST_NAME,
  IncomingPositionVatRateService
} from '../../../../services/incoming-position-vat-rate.service';
import { selectReportingSettings } from 'projects/workspace/src/app/store/selectors/shared.selectors';

@Component({
  selector: 'rnpl-scheduled-payment-position-modal',
  templateUrl: './scheduled-payment-position-modal.component.html',
  styleUrls: ['./scheduled-payment-position-modal.component.scss']
})
export class ScheduledPaymentPositionModalComponent extends BaseModalComponent implements OnInit {

  public companyProfile: CompanyProfile;
  public reportingPeriodPreferences: ReportingPeriodsPreferencesModel;
  public productTypeRadio: 'common'|'general' = 'general';
  // public previousDropdownPosition: 'bottom'|'top'|'auto' = null;

  public productTypes = ProductTypes;
  public purposeType = PurposeTypeEnum;


  public selectedProductInvalid: boolean = false;
  public productNameInvalid: boolean = false;

  public productsList: ProductModel[] = [];
  public generalCategoriesSourceList: any[] = [];

  public accountingSettings: PositionAccountingSettings;
  public selectedProduct: ProductModel = null;
  public positionName: string = null;
  public selectedProductType: ProductTypes = ProductTypes.GOODS;
  public selectedGeneralCategory: any = null;
  public currentCurrency: string;
  public form: FormGroup;
  public customSearchFn = CustomSearchFn;
  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];
  public correctionPositionTypesList = CORRECTION_POSITION_TYPES_LIST;
  public currencySymbolByIsoCode = CURRENCIES_SYMBOLS_BY_ISO_CODE;
  // public purposeList = PURPOSES_LIST;
  public previousUnitType: string;

  readonly generalProductsList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  readonly generalCategoriesFilteredList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  readonly showDropdownSpin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly addPositionRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('positionAccountingSettings', { static: false }) positionAccountingSettingsRef: PositionAccountingSettingsComponent;


  constructor(
    public store: Store<AppState>,
    public toasterService: ToasterService,
    public translateService: TranslateService,
    private productsService: ProductsService,
    private scheduledPaymentApiService: ScheduledPaymentApiService,
    public dialogRef: MatDialogRef<ScheduledPaymentPositionModalComponent>,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private recalculatePositionApiService: RecalculatePositionApiService,
    private productUnitApiService: ProductUnitApiService,
    private incomingPositionVatRateService: IncomingPositionVatRateService,
    @Inject(MAT_DIALOG_DATA) public data: {
      scheduledPayment: ScheduledPaymentModel,
      scheduledPaymentId: number,
      currentCurrency: string,
      vatBlocked: boolean,
      disabledAccountingSettings: boolean,
    }
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.currentCurrency = this.data.currentCurrency;

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });

    this.store.select(selectReportingSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((preferences: ReportingPeriodsPreferencesModel) => {
        this.reportingPeriodPreferences = preferences;
      });
  }

  ngOnInit() {
    this.initForm();
    this.getProducts();
    this.getGeneralProductsListDropdown();
  }

  private initForm() {
    this.form = this.fb.group({
      grossTotalPrice: [null, Validators.required],
      grossUnitPrice: [null, Validators.required],
      netTotalPrice: [null, Validators.required],
      productDescription: [],
      providedServices: [],
      description: [],
      netUnitPrice: [null, Validators.required],
      vat: [null],
      quantity: [1],
      purpose: [PurposeTypeEnum.INTERNAL_USAGE],
      remark: [],
      correctionType: [],
      addRemark: [false],
      productType: [],
      unitType: ['pcs'],
      vatTotal: [],
      specifyAmountsSeparately: [],
      productDescriptionUpdated: [false]
    });
  }

  private getProducts(): void {
    this.productsService.getActiveProducts()
      .pipe(takeUntil(this._destroy))
      .subscribe((products: ProductModel[]) => {
        this.productsList = this.prepareProductsList(products);
        this.showDropdownSpin$.next(false);
      });
  }

  private getGeneralProductsListDropdown(): void {
    this.productsService.getGeneralProductsListDropdown()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        const filteredCategories = response
          .map(category => ({
            ...category,
            children: category.children.filter(c => c.standaloneAllowed)
          }))
          .filter(c => c.children && c.children.length)
          .filter(c => c.isVisibleForList);
        this.generalCategoriesSourceList = filteredCategories;
        this.generalCategoriesFilteredList$.next(filteredCategories);
      });
  }

  public selectSearch(e): void {
    if (this.productTypeRadio === 'general') {
      if (e.term) {
        const filtered = this.generalCategoriesSourceList.filter(category => CustomSearchFn(e.term, category));
        this.generalCategoriesFilteredList$.next(filtered);
      } else {
        this.resetGeneralCategoriesList();
      }
    }
  }

  public resetGeneralCategoriesList(): void {
    this.generalCategoriesFilteredList$.next(this.generalCategoriesSourceList);
  }

  public clearProductsList(): void {
    this.generalProductsList$.next([]);
    this.resetGeneralCategoriesList();
  }

  public selectCategory(category): void {
    this.selectedGeneralCategory = category;
    this.generalProductsList$.next(category.children);
  }

  public fixDropdownPosition(productType: 'common'|'general', dropdownPosition: 'bottom'|'top'|'auto'): void {
    // if (productType === 'general') {
    //   this.previousDropdownPosition = dropdownPosition;
    // } else {
    //   this.previousDropdownPosition = null;
    // }
  }

  public prepareProductsList(products): ProductModel[] {
    return products.map(item => ({
      ...item,
      searchLabel: getProductSearchValue(item, this.translateService)
    }));
  }

  public createProduct(productType: ProductTypes): void {
    const dialog = this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: productType
      },
      disableClose: true,
    });

    dialog.afterClosed().subscribe((product: ProductModel) => {
      if (product) {
        this.productsList.push(product);
        this.selectedProduct = product;
        this.selectedProductType = product.productType;
        this.selectPositionChanges(product);
      }
    });
  }

  public addPosition(): void {
    let description: string = null;

    if (this.selectedProductType === ProductTypes.ARBITRARY || this.selectedProductType === ProductTypes.CORRECTION) {
      if (!this.description.valid) {
        this.productNameInvalid = true;
        return;
      }
    } else {
      if (!this.selectedProduct) {
        this.selectedProductInvalid = true;
        return;
      }
    }

    if (this.addPositionRequest$.getValue()) { return; }
    this.addPositionRequest$.next(true);

    if (get(this.selectedProduct, 'logistics')) {
      description = this.selectedProduct.description;
    }

    const position: Partial<AccountingPositionModel> = {
      ...this.form.getRawValue(),
      productTypeForGrouping: this.selectedProductType,
      manufacturerCode: get(this.selectedProduct, 'manufacturer_code') || get(this.selectedProduct, 'manufacturerCode'),
      productId: +this.selectedProduct.id,
      runpleId: this.selectedProduct.runpleId || this.selectedProduct.runple_id,
      type: this.selectedProductType,
      logistics: this.selectedProduct ? !!this.selectedProduct.logistics : false,
      productDescription: !this.isServicesProductType && !this.isGeneralServiceType
        ? this.productDescription.value
        : description,
      // productDescription: this.selectedProduct.description,
      description: !this.isCorrectionProductType ? get(this.selectedProduct, 'name') : this.description.value,
      providedServices: this.isServicesProductType || this.isGeneralServiceType
        ? this.providedServices.value
        : null,
      documentId: this.data.scheduledPaymentId,
      purpose: this.getPurposeValue(),
      accountingSettings: {
        ...this.accountingSettings,
        ...(this.positionAccountingSettingsRef && this.positionAccountingSettingsRef.getFormValue())
      }
    };

    this.scheduledPaymentApiService.createPositionByPredefinedForm(this.data.scheduledPaymentId, position)
      .pipe(finalize(() => this.addPositionRequest$.next(false)))
      .subscribe((response: AccountingPositionsGroupModel) => this.dialogRef.close(response));
  }

  public updateDescriptionByProductType(): void {
    if (this.isServicesProductType || this.isGeneralServiceType) {
      this.updatePosition(this.providedServices.value, 'providedServices');
    } else {
      this.updatePosition(this.productDescription.value, 'productDescription');
    }
  }

  public updateDescription(description: string): void {
    this.selectedProduct = {
      ...this.selectedProduct,
      description: description
    };
  }

  public updatePosition(value, fieldName, force: boolean = false): void {
    if (!this.selectedProduct) { return; }

    let description: string = null;

    if (get(this.selectedProduct, 'logistics')) {
      description = this.selectedProduct.description;
    }

    if (fieldName === 'specifyAmountsSeparately' && !value && !force) {
      if (
        (this.netTotalPrice.value && (this.netTotalPrice.value !== '0.00')) ||
        (this.grossTotalPrice.value && (this.grossTotalPrice.value !== '0.00')) ||
        (this.vatTotal.value && (this.vatTotal.value !== 0))
      ) {
        const dialog = this.dialog.open(WarningModalComponent, {
          data: CHANGE_CORRECTION_CALCULATION_MODAL_DATA
        });

        dialog.afterClosed().subscribe(res => {
          if (res === CommonModalsActionsEnum.CONFIRM) {
            this.updatePosition(value, fieldName, true);
          } else {
            this.specifyAmountsSeparately.setValue(true);
          }
        });
        return;
      }
    }

    this.addPositionRequest$.next(true);

    this.recalculatePositionApiService.recalculatePosition(DocumentTypesUppercaseEnum.SP, {
      protoPosition: {
        ...this.form.getRawValue(),
        productId: this.selectedProduct.id,
        purpose: this.getPurposeValue(),
        productType: this.selectedProductType,
        productTypeForGrouping: this.selectedProductType,
        productDescription: !this.isServicesProductType && !this.isGeneralServiceType
          ? this.productDescription.value
          : description,
        // productDescription: this.selectedProduct.description,
        providedServices: this.isServicesProductType || this.isGeneralServiceType
          ? this.providedServices.value
          : null,
        description: this.isCorrectionProductType ? this.form.get('description').value : get(this.selectedProduct, 'name'),
        logistics: this.selectedProduct ? !!this.selectedProduct.logistics : false,
        accountingSettings: {
          ...this.accountingSettings,
          ...(this.positionAccountingSettingsRef && this.positionAccountingSettingsRef.getFormValue())
        }
      },
      fieldUpdateRequest: {
        fieldName: fieldName,
        fieldValue: value
      },
      type: this.selectedProductType,
      documentId: this.data.scheduledPaymentId,
    })
      .pipe(finalize(() => this.addPositionRequest$.next(false)))
      .subscribe(product => {
        this.form.patchValue(product);
        this.previousUnitType = product.unitType;
        if (product.accountingSettings) {
          this.accountingSettings = product.accountingSettings;
        }
      }, () => {
        if (fieldName === 'unitType') {
          // set previous value of unit type in case of error
          this.unitType.patchValue(this.previousUnitType);
          // redraw quantity control view
          setTimeout(() => this.quantity.patchValue(this.quantity.value), 10);
        }
      });
  }

  public getProductById(productId) {
    this.productsService.getProduct(productId, DEFAULT_SALES_PRICE_LIST_NAME)
      .pipe(takeUntil(this._destroy))
      .subscribe((product: ProductModel) => this.initializeProduct(product));
  }

  private initializeProduct(product: ProductModel): void {
    this.unitType.patchValue(product.unitType || product.unit_type);
    this.accountingSettings = product.accountingSettings;
    this.productDescription.patchValue(product.description);
    this.providedServices.patchValue(product.description);

    this.incomingPositionVatRateService.resolve(
      product,
      this.companyProfile,
      this.data.vatBlocked,
      this.selectedProductType,
    )
      .pipe(takeUntil(this._destroy))
      .subscribe((vat: number) => {
        this.vat.patchValue(vat);
        // init position predefined data
        // wait for this.positionAccountingSettingsRef to render
        setTimeout(() => this.updatePosition(this.getPurposeValue(), 'purpose'), 200);
      });
  }

  selectPositionChanges(selectedProduct: ProductModel): void {
    this.selectedProduct = selectedProduct;
    this.selectedProductType = selectedProduct.type || selectedProduct.productType;
    if (this.productTypeRadio === 'general') {
      this.selectedProductType = ProductTypes.GENERAL;
    }

    this.accountingSettings = null;
    if (this.positionAccountingSettingsRef) {
      this.positionAccountingSettingsRef.resetForm();
    }

    if (selectedProduct.accountingSettings) {
      this.accountingSettings = selectedProduct.accountingSettings;
      this.accountingSettings.productTypeId = selectedProduct.accountingSettings.glProductTypeId;
    }

    this.productTypeControl.setValue(selectedProduct.type);
    this.unitType.patchValue(selectedProduct.unit_type || 'pcs');

    if (this.data.vatBlocked) {
      this.vat.setValue(0);
      this.vat.disable({onlySelf: true, emitEvent: false});
      this.vatTotal.disable({onlySelf: true, emitEvent: false});
    }

    if (this.selectedProduct.id && this.selectedProductType === ProductTypes.GENERAL) {
      this.initializeProduct(this.selectedProduct);
    } else if (this.selectedProduct.id) {
      this.getProductById(this.selectedProduct.id);
    } else {
      // wait <rnpl-position-accounting-settings> to draw
      setTimeout(() => this.updatePosition(this.getPurposeValue(), 'purpose'), 200);
    }

    this.getProductUnits();
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(this.selectedProduct.type || this.selectedProduct.productType || this.selectedProduct.entityForm)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        this.unitsList = unitsList.map(u => u.name);
      });
  }

  public displayCorrectionPosition(): void {
    this.selectedProductType = ProductTypes.CORRECTION;
    this.selectedProduct = { name: this.translateService.instant('FORM.CORRECTION') };
    this.positionName = null;
    this.initForm();
    this.correctionType.setValue('NEGATIVE');
    if (this.data.vatBlocked) {
      this.vatTotal.disable({onlySelf: true, emitEvent: false});
      this.vat.setValue(0);
    }

    setTimeout(() => this.updatePosition(null, 'purpose'), 200);
  }

  public createGeneralProduct(): void {
    const dialog = this.dialog.open(CreateGeneralProductModalComponent,
      {
        disableClose: true,
        data: {
          familyId: this.selectedGeneralCategory && this.selectedGeneralCategory.categoryId,
          availableForIncomingDocs: true,
          availableForIncomingDocsDisabled: true,
        }
      }
    );
    // TODO: rules — handle the product of the general ledger API (GlProductTypeProductModel) when the modal switches to it
    dialog.afterClosed().subscribe((product: CompositeProductModel) => {
      if (product && product.id) {
        this.selectPositionChanges(product);
      }
    });
  }

  public purposeChanged(): void {
    // if (this.purpose.value === PurposeTypeEnum.RESALE) {
    //   // set to 'common' to hide general products if this.productTypeRadio === 'general'
    //   this.productTypeRadio = 'common';
    // }

    // updatePositionData
    this.updatePosition(this.purpose.value || PurposeTypeEnum.INTERNAL_USAGE, 'purpose');
  }

  public getPurposeValue(): PurposeTypeEnum {
    let purpose;
    if (this.isGoodsProductType || this.isGeneralGoodsType || this.isGeneralDigitalType) {
      purpose = PurposeTypeEnum.INTERNAL_USAGE;
    } else if (this.isDigitalProductType) {
      purpose = this.purpose.value || PurposeTypeEnum.INTERNAL_USAGE;
    } else {
      purpose = null;
    }

    return purpose;
  }

  get isServicesProductType(): boolean {
    return this.selectedProductType === ProductTypes.SERVICES;
  }
  get isGoodsProductType(): boolean {
    return this.selectedProductType === ProductTypes.GOODS;
  }
  get isDigitalProductType(): boolean {
    return this.selectedProductType === ProductTypes.DIGITAL;
  }
  get isCorrectionProductType(): boolean {
    return this.selectedProductType === ProductTypes.CORRECTION;
  }
  get isGeneralProductType(): boolean {
    return this.selectedProductType === ProductTypes.GENERAL;
  }
  get isGeneralServiceType(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.SERVICES;
  }
  get isGeneralDigitalType(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.DIGITAL;
  }
  get isGeneralGoodsType(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.GOODS;
  }
  // get isResale(): boolean {
  //   return this.purpose.value === PurposeTypeEnum.RESALE;
  // }

  get allowFractionalValues(): boolean {
    if (!this.unitType.value || !this.unitsListSource.length) { return true; }
    return this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.unitType.value)
      .allowFractionalValues;
  }

  get priceOptionNet(): boolean {
    return this.data.scheduledPayment.properties.priceOption === PriceOptionEnum.NET;
  }
  get priceOptionGross(): boolean {
    return this.data.scheduledPayment.properties.priceOption === PriceOptionEnum.GROSS;
  }

  get productDescription(): FormControl { return this.form.get('productDescription') as FormControl; }
  get providedServices(): FormControl { return this.form.get('providedServices') as FormControl; }
  get description(): FormControl { return this.form.get('description') as FormControl; }
  get quantity(): FormControl { return this.form.get('quantity') as FormControl; }
  get netUnitPrice(): FormControl { return this.form.get('netUnitPrice') as FormControl; }
  get netTotalPrice(): FormControl { return this.form.get('netTotalPrice') as FormControl; }
  get vat(): FormControl { return this.form.get('vat') as FormControl; }
  get vatTotal(): FormControl { return this.form.get('vatTotal') as FormControl; }
  get grossUnitPrice(): FormControl { return this.form.get('grossUnitPrice') as FormControl; }
  get grossTotalPrice(): FormControl { return this.form.get('grossTotalPrice') as FormControl; }
  get purpose(): FormControl { return this.form.get('purpose') as FormControl; }
  get addRemark(): FormControl { return this.form.get('addRemark') as FormControl; }
  get remark(): FormControl { return this.form.get('remark') as FormControl; }
  get productTypeControl(): FormControl { return this.form.get('productType') as FormControl; }
  get unitType(): FormControl { return this.form.get('unitType') as FormControl; }
  get correctionType(): FormControl { return this.form.get('correctionType') as FormControl; }
  get specifyAmountsSeparately(): FormControl { return this.form.get('specifyAmountsSeparately') as FormControl; }

}
